import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import SEO from "@/components/site/SEO";
import SectionHeading from "@/components/site/SectionHeading";
import AIAgentNetwork from "@/components/site/AIAgentNetwork";
import { BRAND } from "@/data/content";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ContactPage({ onStartProject }) {
    const [form, setForm] = useState({ name: "", email: "", message: "", subject: "" });
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [errors, setErrors] = useState({});

    const setField = (k, v) => {
        setForm((f) => ({ ...f, [k]: v }));
        if (errors[k]) setErrors((e) => ({ ...e, [k]: undefined }));
    };

    const validate = () => {
        const e = {};
        if (!form.email || !/^\S+@\S+\.\S+$/.test(form.email))
            e.email = "Please enter a valid email.";
        if (!form.message || form.message.trim().length < 4)
            e.message = "Please share a short message.";
        return e;
    };

    const submit = async (ev) => {
        ev.preventDefault();
        const e = validate();
        if (Object.keys(e).length) {
            setErrors(e);
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(`${API}/contacts`, {
                name: form.name.trim() || null,
                email: form.email.trim(),
                message: form.message.trim(),
                subject: form.subject.trim() || null,
            });
            setSubmitted(true);
            toast.success("Message received — we'll respond shortly.");
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(
                typeof detail === "string" ? detail : "Something went wrong. Please try again."
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="page-contact">
            <SEO
                title="Contact — Start a Project with IdeaClap"
                description="Tell us what you're trying to build. We'll come back with a point of view, a senior partner, and — if it's a fit — a small team shaped to your outcome. Based in NYC. Working globally."
                path="/contact"
                keywords="contact IdeaClap, hire AI marketing agency, start a project, AI creative agency NYC"
            />
            <header className="relative pt-32 lg:pt-40 pb-16 overflow-hidden">
                <div className="absolute inset-0 hero-radial pointer-events-none" />
                <div className="relative mx-auto max-w-7xl container-px grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                    <div className="lg:col-span-7">
                        <SectionHeading
                            eyebrow="Contact"
                            title={
                                <>
                                    Tell us what you’re trying to build.{" "}
                                    <span className="italic text-[hsl(var(--text-muted))]">We’ll come back with a point of view.</span>
                                </>
                            }
                            subtitle="For new projects use the intake below or open the consultative brief. For everything else, our channels are listed alongside."
                        />
                        <div className="mt-8">
                            <button
                                type="button"
                                onClick={onStartProject}
                                data-testid="contact-page-start-project-button"
                                className="sheen group inline-flex items-center gap-2 rounded-full bg-[hsl(var(--text))] hover:bg-[hsl(var(--text))]/90 text-[hsl(var(--bg))] px-5 py-3 text-[14.5px] font-semibold transition-colors"
                            >
                                Open project intake
                                <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
                            </button>
                        </div>
                    </div>
                    <div className="lg:col-span-5">
                        <div className="relative aspect-square rounded-3xl border border-black/[0.08] bg-white overflow-hidden shadow-deep">
                            <AIAgentNetwork className="absolute inset-0" />
                        </div>
                    </div>
                </div>
            </header>

            <section className="mx-auto max-w-7xl container-px py-16 lg:py-20">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                    <div className="lg:col-span-7">
                        <div className="rounded-3xl border border-black/[0.08] bg-white p-7 sm:p-8 shadow-deep">
                            <div className="flex items-center justify-between">
                                <h3 className="font-display text-2xl text-[hsl(var(--text))]">
                                    Send us a message
                                </h3>
                                <span className="text-[11px] font-mono uppercase tracking-[0.22em] text-[hsl(var(--text-subtle))]">
                                    24h response
                                </span>
                            </div>
                            {submitted ? (
                                <motion.div
                                    initial={{ opacity: 0, y: 8 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.4 }}
                                    className="mt-8 flex flex-col items-start gap-3"
                                    data-testid="contact-form-success"
                                >
                                    <div className="h-12 w-12 rounded-full bg-[rgba(187,118,33,0.10)] ring-1 ring-[rgba(187,118,33,0.35)] inline-flex items-center justify-center">
                                        <CheckCircle2 className="h-6 w-6 text-[hsl(var(--gold))]" />
                                    </div>
                                    <div className="font-display text-2xl text-[hsl(var(--text))]">
                                        Message received — we’ll respond shortly.
                                    </div>
                                </motion.div>
                            ) : (
                                <form
                                    onSubmit={submit}
                                    className="mt-6 flex flex-col gap-5"
                                    data-testid="contact-form"
                                    noValidate
                                >
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <Field label="Your name">
                                            <input
                                                type="text"
                                                value={form.name}
                                                onChange={(e) => setField("name", e.target.value)}
                                                className="w-full rounded-xl bg-[hsl(var(--bg-2))] ring-1 ring-inset ring-black/10 focus-visible:ring-2 focus-visible:ring-[hsl(var(--blue))] text-[14.5px] text-[hsl(var(--text))] placeholder:text-[hsl(var(--text-subtle))] px-4 py-3 outline-none"
                                                placeholder="Jane Doe"
                                                data-testid="contact-name"
                                            />
                                        </Field>
                                        <Field label="Email" error={errors.email}>
                                            <input
                                                type="email"
                                                value={form.email}
                                                onChange={(e) => setField("email", e.target.value)}
                                                className="w-full rounded-xl bg-[hsl(var(--bg-2))] ring-1 ring-inset ring-black/10 focus-visible:ring-2 focus-visible:ring-[hsl(var(--blue))] text-[14.5px] text-[hsl(var(--text))] placeholder:text-[hsl(var(--text-subtle))] px-4 py-3 outline-none"
                                                placeholder="jane@company.com"
                                                data-testid="contact-email-input"
                                            />
                                        </Field>
                                    </div>
                                    <Field label="Subject" hint="Optional">
                                        <input
                                            type="text"
                                            value={form.subject}
                                            onChange={(e) => setField("subject", e.target.value)}
                                            className="w-full rounded-xl bg-[hsl(var(--bg-2))] ring-1 ring-inset ring-black/10 focus-visible:ring-2 focus-visible:ring-[hsl(var(--blue))] text-[14.5px] text-[hsl(var(--text))] placeholder:text-[hsl(var(--text-subtle))] px-4 py-3 outline-none"
                                            placeholder="Press, partnerships, talent network…"
                                            data-testid="contact-subject"
                                        />
                                    </Field>
                                    <Field label="Message" error={errors.message}>
                                        <textarea
                                            rows={4}
                                            value={form.message}
                                            onChange={(e) => setField("message", e.target.value)}
                                            className="w-full rounded-xl bg-[hsl(var(--bg-2))] ring-1 ring-inset ring-black/10 focus-visible:ring-2 focus-visible:ring-[hsl(var(--blue))] text-[14.5px] text-[hsl(var(--text))] placeholder:text-[hsl(var(--text-subtle))] px-4 py-3 outline-none"
                                            placeholder="Tell us what's on your mind."
                                            data-testid="contact-message"
                                        />
                                    </Field>
                                    <button
                                        type="submit"
                                        disabled={submitting}
                                        data-testid="contact-submit-button"
                                        className="sheen group mt-1 inline-flex items-center justify-center gap-2 rounded-full bg-[hsl(var(--text))] hover:bg-[hsl(var(--text))]/90 text-[hsl(var(--bg))] px-5 py-3 text-[14px] font-semibold disabled:opacity-70 transition-colors w-full sm:w-auto"
                                    >
                                        {submitting ? (
                                            <>
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                                Sending…
                                            </>
                                        ) : (
                                            <>
                                                Send message
                                                <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
                                            </>
                                        )}
                                    </button>
                                </form>
                            )}
                        </div>
                    </div>
                    <div className="lg:col-span-5 flex flex-col gap-4">
                        <Channel
                            icon={MapPin}
                            label="Headquarters"
                            value={BRAND.contact.address}
                            testid="contact-address"
                        />
                        <Channel
                            icon={Mail}
                            label="Email"
                            href={`mailto:${BRAND.contact.email}`}
                            value={BRAND.contact.email}
                            testid="contact-email"
                        />
                        <Channel
                            icon={Phone}
                            label="Phone"
                            href={`tel:${BRAND.contact.phone.replace(/[^\d+]/g, "")}`}
                            value={BRAND.contact.phone}
                            testid="contact-phone"
                        />
                        <div className="rounded-2xl border border-black/[0.08] bg-white p-6">
                            <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-[hsl(var(--text-subtle))]">
                                Office hours
                            </div>
                            <div className="mt-2 text-[hsl(var(--text))]">
                                Mon – Fri · 9:00 AM – 7:00 PM (ET)
                            </div>
                            <div className="mt-2 text-[hsl(var(--text-muted))] text-[13.5px]">
                                Global teams operate across time zones; partners typically respond within one business day.
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}

const Field = ({ label, hint, error, children }) => (
    <label className="flex flex-col gap-2">
        <span className="flex items-center justify-between">
            <span className="text-[12px] uppercase tracking-[0.16em] font-mono text-[hsl(var(--text-muted))]">
                {label}
            </span>
            {hint && <span className="text-[11px] text-[hsl(var(--text-subtle))]">{hint}</span>}
        </span>
        {children}
        {error && (
            <span className="text-[12px] text-[hsl(var(--destructive))]">{error}</span>
        )}
    </label>
);

const Channel = ({ icon: Icon, label, value, href, testid }) => {
    const content = (
        <div className="flex items-start gap-4 rounded-2xl border border-black/[0.08] bg-white p-5 hover:border-black/15 hover:shadow-soft transition-all duration-300">
            <span className="shrink-0 inline-flex h-10 w-10 items-center justify-center rounded-full bg-[rgba(37,99,235,0.10)] ring-1 ring-[rgba(37,99,235,0.35)] text-[hsl(var(--blue))]">
                <Icon className="h-4 w-4" />
            </span>
            <div>
                <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-[hsl(var(--text-subtle))]">
                    {label}
                </div>
                <div className="mt-1 text-[hsl(var(--text))]" data-testid={testid}>
                    {value}
                </div>
            </div>
        </div>
    );
    return href ? (
        <a href={href} className="block">
            {content}
        </a>
    ) : (
        content
    );
};
